import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { DEFAULT_DEBOUNCE_TIME } from '@modules/bcm/@shared/constants';
import { isString } from '@shared/functions/is-string';
import { matchCountryByInput } from '@shared/functions/match-country-by-input';
import { Country } from '@shared/models/country';
import { Observable } from 'rxjs';
import { CountryApiService } from '@bcmApiServices/country.api-service';

@Component({
    selector: 'form-widget-countries',
    templateUrl: './form-widget-countries.component.html',
    standalone: false
})
export class FormWidgetCountries implements OnInit, OnDestroy, OnChanges {
    @Input()
    parentFormGroup: UntypedFormGroup;

    @Input()
    appearance: MatFormFieldAppearance = 'outline';

    @Input()
    tabIndex: number;

    @Input()
    country: Country;

    @Input()
    defaultCountryCode: string | null = null;

    countries: Country[] = [];

    filteredCountries: Observable<Country[]>;

    private countryControlAddedByWidget = false;

    constructor(
        private _countryApiService: CountryApiService
    ) {
    }

    ngOnInit(): void {
        this._countryApiService.getAll()
            .subscribe(countries => {
                this.countries = countries;
                this._applyDefaultCountryCode();
                this._resolveTypedCountryValue();
            });

        const existingCountryControl = this.parentFormGroup.get('country');

        if (existingCountryControl) {
            if (this.country) {
                existingCountryControl.setValue(this.country, { emitEvent: false });
            }
        } else {
            this.parentFormGroup.addControl('country', new FormControl<any>(this.country || null));
            this.countryControlAddedByWidget = true;
        }

        this.filteredCountries = this.parentFormGroup.get('country').valueChanges.pipe(
            startWith(''),
            debounceTime(DEFAULT_DEBOUNCE_TIME),
            map((value) => isString(value) ? this._filterCountries(value) : this.countries)
        );
    }

    ngOnDestroy() {
        if (this.countryControlAddedByWidget) {
            this.parentFormGroup.removeControl('country');
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            if (changes.hasOwnProperty(propName)) {
                switch (propName) {
                    case 'country': {
                        if (this.country) {
                            this.parentFormGroup?.get('country')?.setValue(this.country, {emitEvent: false});
                        }
                    }
                }
            }
        }
    }

    displayCountryWith(country: Country | string) {
        if (typeof country === 'string') {
            return country;
        }

        if (country) {
            return `${country.de} (${country.code})`;
        }

        return '';
    }

    onCountryBlur(): void {
        this._resolveTypedCountryValue();
    }

    private _resolveTypedCountryValue(): void {
        const formControl = this.parentFormGroup?.get('country');

        if (!formControl || !this.countries.length) {
            return;
        }

        const resolved = matchCountryByInput(formControl.value, this.countries);

        if (resolved && formControl.value !== resolved) {
            formControl.setValue(resolved, { emitEvent: false });
        }
    }

    private _applyDefaultCountryCode(): void {
        if (!this.defaultCountryCode || this.country || !this.countries.length) {
            return;
        }
        const formControl = this.parentFormGroup?.get('country');
        if (!formControl || !formControl.untouched || formControl.value) {
            return;
        }
        const defaultCountry = this.countries.find(c => c.code === this.defaultCountryCode);
        if (defaultCountry) {
            formControl.setValue(defaultCountry);
        }
    }

    private _filterCountries(value: string): Country[] {
        const filterValue = value.toLowerCase();

        return this.countries.filter(country => {
            return country.de.toLowerCase().includes(filterValue)
                || (country.code || '').toLowerCase().includes(filterValue);
        });
    }

}
